import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Department,
  DepartmentHierarchyNodeFragmentFragment as DepartmentHierarchyNode,
  useDepartmentChildrenLazyQuery,
  useDepartmentHierarchyLazyQuery,
} from 'graphql-common';
import { TableTreeProps, TableTreeRowState } from '@lib/components/Table/types';

type Args = {
  collection: Department[];
  searchQuery?: string;
  /**
   * True when a non-search filter (e.g. Managers) is applied. Filtered
   * results are a flat match list, same as search — `departmentChildren`
   * fetches a parent's children unfiltered, so overlaying them on a filtered
   * root set would mix in departments that don't match the filter and can
   * collide with ids already present in that root set (duplicate React
   * keys). Treated the same as `isSearchActive` below.
   */
  hasActiveFilters?: boolean;
  /**
   * Ancestors of every id in this list are auto-expanded and preloaded (once
   * per id) — e.g. the current parent selection, or every currently-linked
   * department in a multiselect picker, so nested preselections are visible
   * without the user manually unfolding each level.
   */
  autoExpandIds?: string[];
};

const EMPTY_STATE: TableTreeRowState = {
  depth: 0,
  parentId: null,
  hasChildren: false,
  isExpanded: false,
  isLoadingChildren: false,
};

interface AncestorNode extends DepartmentHierarchyNode {
  parent?: AncestorNode | null;
}

export default function useDepartmentsTableTree({
  collection,
  searchQuery,
  hasActiveFilters,
  autoExpandIds,
}: Args): {
  data: Department[];
  evictNode: (id: string) => void;
  tree: TableTreeProps<Department>;
} {
  const [fetchChildren] = useDepartmentChildrenLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [fetchHierarchy] = useDepartmentHierarchyLazyQuery({
    fetchPolicy: 'cache-and-network',
  });
  const [childrenByParentId, setChildrenByParentId] = useState<
    Record<string, Department[]>
  >({});
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [loadingIds, setLoadingIds] = useState<Set<string>>(new Set());
  const isSearchActive = !!searchQuery?.trim();
  const isFlatView = isSearchActive || !!hasActiveFilters;

  const loadChildren = useCallback(
    async (parentId: string) => {
      setLoadingIds((previous) => new Set(previous).add(parentId));
      try {
        const { data } = await fetchChildren({ variables: { id: parentId } });
        const children = (data?.data?.children || []) as Department[];
        setChildrenByParentId((previous) => ({
          ...previous,
          [parentId]: children,
        }));
      } finally {
        setLoadingIds((previous) => {
          const next = new Set(previous);
          next.delete(parentId);
          return next;
        });
      }
    },
    [fetchChildren],
  );

  // Auto-expand and preload every ancestor above each id in `autoExpandIds`.
  // Each id is only resolved once (tracked via ref, since these ids are
  // fetched imperatively — one `departmentHierarchy` lookup per id — and
  // aren't otherwise reflected in component state).
  const resolvedAutoExpandIdsRef = useRef<Set<string>>(new Set());
  const requestedAutoExpandChildrenRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    const idsToResolve = (autoExpandIds || []).filter(
      (id) => id && !resolvedAutoExpandIdsRef.current.has(id),
    );
    if (!idsToResolve.length) return;
    idsToResolve.forEach((id) => resolvedAutoExpandIdsRef.current.add(id));

    idsToResolve.forEach((id) => {
      fetchHierarchy({ variables: { id } }).then(({ data }) => {
        // Ancestor chain, root-first, ending with the id itself.
        const chain: string[] = [];
        let node = data?.data as AncestorNode | undefined;
        while (node) {
          chain.unshift(node.id);
          node = node.parent || undefined;
        }
        const ancestorsAboveSelected = chain.slice(0, -1);
        if (!ancestorsAboveSelected.length) return;
        setExpandedIds(
          (previous) => new Set([...previous, ...ancestorsAboveSelected]),
        );
        ancestorsAboveSelected.forEach((ancestorId) => {
          if (!requestedAutoExpandChildrenRef.current.has(ancestorId)) {
            requestedAutoExpandChildrenRef.current.add(ancestorId);
            loadChildren(ancestorId);
          }
        });
      });
    });
  }, [autoExpandIds, fetchHierarchy, loadChildren]);

  const toggleRow = useCallback(
    (item: Department) => {
      if (isFlatView) return;
      const isExpanded = expandedIds.has(String(item.id));
      setExpandedIds((previous) => {
        const next = new Set(previous);
        if (isExpanded) next.delete(String(item.id));
        else next.add(String(item.id));
        return next;
      });
      if (
        !isExpanded &&
        !childrenByParentId[String(item.id)] &&
        !loadingIds.has(String(item.id))
      ) {
        loadChildren(String(item.id));
      }
    },
    [childrenByParentId, expandedIds, isFlatView, loadChildren, loadingIds],
  );

  const { data, rowStates } = useMemo(() => {
    const states = new Map<string, TableTreeRowState>();
    if (isFlatView) {
      collection.forEach((item) => {
        states.set(String(item.id), EMPTY_STATE);
      });
      return { data: collection, rowStates: states };
    }

    const rows: Department[] = [];
    const appendRows = (
      items: Department[],
      depth: number,
      parentId: string | null,
      ancestors: Set<string>,
    ) => {
      items.forEach((item) => {
        const id = String(item.id);
        if (ancestors.has(id)) return;
        const state: TableTreeRowState = {
          depth,
          parentId,
          hasChildren: Number(item.childrenCount) > 0,
          isExpanded: expandedIds.has(id),
          isLoadingChildren: loadingIds.has(id),
        };
        states.set(id, state);
        rows.push(item);
        if (state.isExpanded && state.hasChildren) {
          appendRows(
            childrenByParentId[id] || [],
            depth + 1,
            id,
            new Set([...ancestors, id]),
          );
        }
      });
    };
    appendRows(collection, 0, null, new Set());
    return { data: rows, rowStates: states };
  }, [childrenByParentId, collection, expandedIds, isFlatView, loadingIds]);

  const getRowState = useCallback(
    (item: Department) => rowStates.get(String(item.id)) || EMPTY_STATE,
    [rowStates],
  );

  // Drops a deleted department from every cached children list (and its own
  // cached children, if it had been expanded) so a destroyed row doesn't
  // linger in an already-loaded parent's children until a full remount.
  const evictNode = useCallback((id: string) => {
    const key = String(id);
    setChildrenByParentId((previous) => {
      let changed = false;
      const next: Record<string, Department[]> = {};
      Object.entries(previous).forEach(([parentId, children]) => {
        if (parentId === key) {
          changed = true;
          return;
        }
        if (children.some((child) => String(child.id) === key)) {
          changed = true;
          next[parentId] = children.filter((child) => String(child.id) !== key);
        } else {
          next[parentId] = children;
        }
      });
      return changed ? next : previous;
    });
  }, []);

  return {
    data,
    evictNode,
    tree: {
      getRowState,
      onToggleRow: toggleRow,
      rootCount: collection.length,
      isFlatView,
    },
  };
}
